import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AppState } from '../../../lib/store';
import { useRescueContext } from '../../lib/RescueContext';
import { logout } from '../../../lib/authentication';
import { clearImportJobKey } from '../../lib/importJobCookie';
import { trackAccountMenuOpened } from '../../analytics';
import styles from './Header.module.scss';

/** "Great Dog Rescue…" → "GD". Empty org names fall back to "Fi". */
function orgInitials(orgName: string): string {
  const words = orgName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return 'Fi';
  }
  return words
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

export default function Header() {
  const location = useLocation();
  const session = useSelector((state: AppState) => state.session);
  const { orgName, setSidebarOpen } = useRescueContext();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const onAccountPage = location.pathname.startsWith('/rescue/account');

  // Close the org menu on outside click or Escape.
  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    function handleMouseDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  // Close when navigating (e.g. after clicking Manage Account).
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleToggleMenu = () => {
    if (!menuOpen) {
      trackAccountMenuOpened();
    }
    setMenuOpen(!menuOpen);
  };

  const handleLogout = async () => {
    // Drop the saved CSV-import resume pointer so the next user on a shared
    // browser doesn't inherit it (and hit a Forbidden when resuming).
    clearImportJobKey();
    try {
      await logout();
    } catch {
      // Redirect handled by logout
    }
  };

  return (
    <div className={styles.headerWrapper}>
      <header className={styles.header}>
        <div className={styles.left}>
          <button className={styles.mobileMenuBtn} onClick={() => setSidebarOpen(true)} aria-label="Toggle menu">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>

        <div className={styles.actions}>
          <div className={styles.orgMenu} ref={menuRef}>
            <button
              type="button"
              className={`${styles.orgTrigger} ${menuOpen ? styles.orgTriggerOpen : ''}`}
              onClick={handleToggleMenu}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <span className={styles.orgAvatar}>{orgInitials(orgName)}</span>
              <span className={styles.orgTriggerName}>{orgName}</span>
              <span className={`${styles.orgChevron} ${menuOpen ? styles.orgChevronUp : ''}`}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>

            {menuOpen && (
              <div className={styles.orgDropdown} role="menu">
                <div className={styles.dropdownHead}>
                  <div className={styles.dropdownOrg}>{orgName}</div>
                  {session?.email && <div className={styles.dropdownEmail}>{session.email}</div>}
                </div>
                <Link
                  to="/rescue/account"
                  role="menuitem"
                  className={`${styles.dropdownItem} ${onAccountPage ? styles.dropdownItemActive : ''}`}
                  onClick={() => setMenuOpen(false)}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Manage Account
                </Link>
                <button type="button" role="menuitem" className={styles.dropdownItem} onClick={handleLogout}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      {session?.impersonating && (
        <div className={styles.impersonationBanner}>You are impersonating {session.email}</div>
      )}
    </div>
  );
}
